import {
  collection, deleteDoc, doc, getDocs, limit, onSnapshot, orderBy, query,
  serverTimestamp, setDoc, updateDoc, where, Timestamp, type WithFieldValue,
} from "firebase/firestore";
import { getDownloadURL, ref } from "firebase/storage";
import {
  marcasValidas, RULES_VERSION,
  type AppDeDelivery, type Canal, type DayKey, type Plate as CorePlate,
} from "@nhomi/core";
import { daPlataforma, db, storage, type Progresso } from "./ambiente";
import type { Me } from "./challenges";
import { perfilQueVale } from "./users";
import type { PlateDoc } from "./schema";

export interface Plate extends PlateDoc { id: string }

const plateRef = (cid: string, pid: string) => doc(db, "challenges", cid, "plates", pid);
const platesRef = (cid: string) => collection(db, "challenges", cid, "plates");

/* ---------- dia ---------- */

/** Dia no fuso de quem postou: "2026-08-06". */
export function dayKey(date: Date = new Date()): DayKey {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}`;
}

export const plateDate = (p: Plate): Date =>
  p.at?.toDate?.() ?? p.createdAt?.toDate?.() ?? new Date(0);

/* ---------- fotos ---------- */

/**
 * URL de uma foto no Storage, com cache em memória.
 *
 * `getDownloadURL` é uma chamada de rede por imagem. Sem o cache, rolar o
 * feed para cima e para baixo refaz todas elas.
 *
 * E ELA ACEITA URL PRONTA, o que não é preciosismo: o campo `photo` de
 * um usuário guarda DUAS coisas diferentes conforme o caminho que a
 * pessoa fez. Quem trocou a foto à mão tem um caminho do Storage
 * (`users/{uid}/…`); quem só entrou com o Google tem a URL do
 * `lh3.googleusercontent.com` que veio do login. As duas coexistem no
 * mesmo campo — o site diz isso em voz alta na tela de Conta e se
 * protege com um `startsWith("http")`, e a migração não trouxe essa
 * linha.
 *
 * O preço foi um APP FECHANDO: `ref(storage, "https://lh3…")` LANÇA na
 * hora, de forma sincrona, com "Firebase Storage: Invalid URL". Dentro
 * de um `useEffect` isso não é promessa rejeitada que um `.catch`
 * apanha: é erro de renderização, e ele derruba a árvore inteira. Era
 * isso o "clicar no meu perfil crasha o app" e o "configurações
 * inacessível" — as duas telas desenham a foto de quem entrou com o
 * Google.
 *
 * O `try` em volta do `ref` é o cinto de segurança do resto: caminho
 * malformado por qualquer outro motivo devolve `null` e a tela mostra o
 * quadrado cinza, em vez de fechar o app.
 */
const urlCache = new Map<string, Promise<string>>();

export function photoURL(path: string | null | undefined): Promise<string | null> {
  if (!path) return Promise.resolve(null);
  /* URL já pronta é devolvida como está: o Storage não tem o que
     resolver, e pedir para ele resolver é o que estourava. */
  if (/^https?:\/\//i.test(path)) return Promise.resolve(path);

  const cached = urlCache.get(path);
  if (cached) return cached;

  let pending: Promise<string>;
  try {
    pending = getDownloadURL(ref(storage, path)).catch(() => "");
  } catch {
    pending = Promise.resolve("");
  }
  urlCache.set(path, pending);
  return pending.then((u) => u || null);
}

/* ---------- publicar ---------- */

export interface NovoPrato<Foto = unknown> {
  title: string;
  description: string;
  mealId: string | null;
  cuisineId: string | null;
  homemade: boolean;
  price: number | null;
  place: string;
  coords: { lat: number; lng: number } | null;
  /** Onde foi comprado — só em prato comprado, e opcional. */
  canal?: Canal | null;
  /** O app de entrega, só com `canal: "delivery"`. */
  appDelivery?: AppDeDelivery | null;
  /** "Comentários e notas" desligado por quem postou. */
  fechado?: boolean;
  /**
   * Uma ou mais fotos do MESMO prato, já reduzidas. A primeira é a capa.
   *
   * O TIPO É DA PLATAFORMA, e é por isso que ele é um parâmetro: no site
   * uma foto pronta é um `Blob` saído do canvas; no app é um
   * `{ uri, bytes, tipo }`, porque o SDK do Firebase não monta Blob de
   * `Uint8Array` no React Native e quem monta é o `fetch` na hora do
   * envio. Quem recebe isto aqui só repassa para o enviador do lado
   * certo, então o tipo atravessa sem ser olhado — e continua checado na
   * ponta, que é onde erra.
   *
   * LISTA VAZIA é o prato sem foto (set/2026): custa no placar, e a
   * descrição passa a ser obrigatória — é o que a IA lê no lugar da foto.
   * A regra do Firestore recusa sem foto e sem descrição.
   */
  fotos: { photo: Foto; thumb: Foto }[];
  at: Date;
  dateEdited: boolean;
  /** Outros desafios onde este mesmo prato deve aparecer. O app só grava
      a lista; quem copia é o servidor, com o mesmo id de documento e
      apontando para a foto que já subiu aqui. */
  replicas?: string[];
}

/**
 * Publica um prato.
 *
 * O id do documento é gerado ANTES do upload, para as fotos já subirem no
 * caminho definitivo — sem isso seria preciso criar o documento, subir e
 * atualizar, e um upload que falha no meio deixaria um prato sem foto no
 * feed de todo mundo.
 *
 * Nada de pontuação é escrito aqui. Quem pontua é a Cloud Function, e as
 * regras do Firestore recusariam a tentativa de qualquer jeito.
 */
export async function createPlate<Foto>(
  cid: string,
  me: Me,
  input: NovoPrato<Foto>,
  aoProgredir?: (p: Progresso) => void,
): Promise<string> {
  const { extensao, subirTodos } = daPlataforma();
  /* Autor é o de `users/{uid}`: o `me` traz o nome e a foto do login,
     que a troca em Conta não atualiza. */
  const autor = await perfilQueVale(me);
  const pid = doc(platesRef(cid)).id;
  const base = `challenges/${cid}/plates/${me.uid}/${pid}`;

  /* As fotos chegam PRONTAS. Elas são reduzidas na hora em que a pessoa
     escolhe cada uma, não aqui: o `File` da câmera do Android aponta pra
     um arquivo temporário que some quando a câmera é aberta de novo, e
     guardar essa referência até o publish fazia os bytes evaporarem. */
  const prontas = input.fotos;

  const photos = prontas.map((_, i) => ({
    // A primeira mantém o nome sem sufixo: é o caminho que os pratos já
    // postados usam, e mudá-lo quebraria as fotos que já estão no ar.
    photo: `${base}${i ? `-${i}` : ""}.${extensao}`,
    thumb: `${base}${i ? `-${i}` : ""}-thumb.${extensao}`,
  }));

  await subirTodos(
    prontas.flatMap((p, i) => [
      { caminho: photos[i]!.photo, dados: p.photo },
      { caminho: photos[i]!.thumb, dados: p.thumb },
    ]),
    aoProgredir,
  );

  const photoPath = photos[0]?.photo ?? null;
  const thumbPath = photos[0]?.thumb ?? null;
  const comprado = !input.homemade;
  const canal = comprado ? input.canal ?? null : null;

  await setDoc(plateRef(cid, pid), {
    uid: autor.uid,
    authorName: autor.name,
    authorPhoto: autor.photo,
    title: input.title,
    description: input.description,
    mealId: input.mealId,
    cuisineId: input.cuisineId,
    homemade: input.homemade,
    photoPath,
    thumbPath,
    photos,
    place: input.place,
    placeKey: placeKey(input.place),
    coords: input.coords,
    price: input.price,
    day: dayKey(input.at),
    at: Timestamp.fromDate(input.at),
    createdAt: serverTimestamp(),
    dateEdited: input.dateEdited,
    // Lista vazia nunca é gravada: campo ausente é mais barato de ler e
    // deixa o gatilho de replicação sair na primeira linha.
    ...(input.replicas?.length ? { replicas: input.replicas } : {}),
    // Mesmo espírito: só grava o que existe. Prato de casa não tem canal.
    ...(photoPath ? {} : { semFoto: true }),
    ...(input.fechado ? { fechado: true } : {}),
    ...(canal ? { canal } : {}),
    ...(canal === "delivery" && input.appDelivery ? { appDelivery: input.appDelivery } : {}),
    // Campos do servidor: entram vazios e a função preenche.
    rulesVersion: RULES_VERSION,
    basePoints: 0,
    mealRepeated: false,
    ratingAvg: null,
    ratingCount: 0,
    commentCount: 0,
    kcal: null,
    kcalSource: null,
  } satisfies WithFieldValue<PlateDoc>);

  return pid;
}

export async function updatePlate(
  cid: string,
  pid: string,
  patch: Partial<Pick<PlateDoc,
    "title" | "description" | "mealId" | "cuisineId" | "homemade" | "price" | "place" | "coords"
    | "canal" | "appDelivery" | "fechado">>
    & { at?: Date },
): Promise<void> {
  const campos: Record<string, unknown> = { ...patch };
  /* O app de entrega só existe junto de "delivery" — a regra recusa o
     resto. Mudar o canal (ou virar comida de casa) leva o app junto. */
  if (patch.homemade === true) { campos["canal"] = null; campos["appDelivery"] = null; }
  else if (patch.canal !== undefined && patch.canal !== "delivery") campos["appDelivery"] = null;
  if (patch.place !== undefined) campos["placeKey"] = placeKey(patch.place);
  if (patch.at) {
    campos["at"] = Timestamp.fromDate(patch.at);
    campos["day"] = dayKey(patch.at);
    campos["dateEdited"] = true;
  }
  await updateDoc(plateRef(cid, pid), campos);
}

/**
 * Apaga o prato — o DOCUMENTO, e só ele.
 *
 * A foto fica para o servidor de propósito. O mesmo prato pode viver em
 * vários desafios, e a cópia aponta para o arquivo que subiu no desafio
 * de origem: um só, compartilhado. O app apagando a foto aqui destruía a
 * imagem das cópias que continuavam vivas — o prato sobrevivia no outro
 * grupo com a foto quebrada, e não havia como desfazer.
 *
 * Quem sabe que a última cópia se foi é o `aoReplicarPrato`, que enxerga
 * os outros desafios. A tela não enxerga, e não tem como enxergar.
 */
export async function deletePlate(cid: string, pid: string): Promise<void> {
  await deleteDoc(plateRef(cid, pid));
}

/** As fotos grandes de um prato, na ordem — inclusive nos pratos antigos. */
export function fotosDoPrato(plate: Plate): string[] {
  if (plate.photos?.length) return plate.photos.map((f) => f.photo);
  return plate.photoPath ? [plate.photoPath] : [];
}

/* ---------- ler ---------- */

/**
 * Feed do desafio.
 *
 * Ordena por `day`, NUNCA por `at`. O Firestore exclui do resultado todo
 * documento que não tem o campo da ordenação — no app anterior um prato
 * sem `at` sumiu do feed inteiro e só foi achado semanas depois. `day` é
 * sempre string e sempre gravado.
 */
export function watchFeed(cid: string, cb: (list: Plate[]) => void, max = 80): () => void {
  const q = query(platesRef(cid), orderBy("day", "desc"), limit(max));
  return onSnapshot(
    q,
    (snap) => {
      // Prato de quem o dono removeu do grupo não aparece aqui.
      const list = snap.docs.map((d) => ({ id: d.id, ...(d.data() as PlateDoc) })).filter((p) => !p.oculto);
      // Dentro do dia, o horário decide.
      list.sort((a, b) => plateDate(b).getTime() - plateDate(a).getTime());
      cb(list);
    },
    () => cb([]),
  );
}

export function watchPlate(cid: string, pid: string, cb: (p: Plate | null) => void): () => void {
  return onSnapshot(
    plateRef(cid, pid),
    (s) => cb(s.exists() ? { id: s.id, ...(s.data() as PlateDoc) } : null),
    () => cb(null),
  );
}

/** Quais refeições a pessoa já postou num dia — o aviso de repetida. */
export function watchMealsOfDay(
  cid: string, uid: string, day: DayKey, cb: (meals: Set<string>) => void,
): () => void {
  const q = query(platesRef(cid), where("uid", "==", uid), where("day", "==", day));
  return onSnapshot(
    q,
    (snap) => cb(new Set(snap.docs.map((d) => (d.data() as PlateDoc).mealId).filter((m): m is string => !!m))),
    () => cb(new Set()),
  );
}

/** "Bar do Zé " e "bar do ze" viram a mesma chave. */
export function placeKey(name: string): string {
  return name
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .toLowerCase().replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 60);
}

/* ---------- consultas por período ---------- */

/**
 * Pratos de uma pessoa num mês, para o calendário e a grade do perfil.
 *
 * Consulta por `day` como STRING, não por `at`: além de o Firestore
 * excluir documentos sem o campo da ordenação, comparar Timestamp exige
 * decidir fuso, e "agosto" é agosto no relógio de quem postou.
 */
export async function monthPlates(
  cid: string, uid: string, year: number, month: number,
): Promise<Plate[]> {
  const mm = String(month + 1).padStart(2, "0");
  const de = `${year}-${mm}-01`;
  const ate = `${year}-${mm}-31`;
  // O `orderBy` explícito casa com o índice `uid ASC, day DESC` que já
  // existe. Sem ele o Firestore ordena por `day` ascendente e pede um
  // índice novo — que só falha em produção, na cara de quem abriu a tela.
  const q = query(
    platesRef(cid),
    where("uid", "==", uid),
    where("day", ">=", de),
    where("day", "<=", ate),
    orderBy("day", "desc"),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as PlateDoc) }));
}

/** Todos os pratos do desafio num intervalo de dias, para o recap. */
export async function periodPlates(
  cid: string, de: DayKey, ate: DayKey, max = 1000,
): Promise<Plate[]> {
  const q = query(
    platesRef(cid),
    where("day", ">=", de),
    where("day", "<=", ate),
    orderBy("day", "desc"),
    limit(max),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as PlateDoc) })).filter((p) => !p.oculto);
}

/** O adaptador para o motor de pontos do `@nhomi/core`. */
export function toCorePlate(p: Plate): CorePlate {
  return {
    id: p.id,
    uid: p.uid,
    day: p.day,
    at: p.at?.toMillis?.() ?? 0,
    homemade: p.homemade,
    mealId: p.mealId,
    cuisineId: p.cuisineId,
    /* Sem a era, vale a de hoje — que é o que o SERVIDOR faz ao somar o
       placar. As cópias de prato replicado nasceram sem este campo até
       set/2026, e a tela as tratava como era antiga, mostrando o
       `basePoints` congelado da cópia: 0. */
    rulesVersion: p.rulesVersion ?? RULES_VERSION,
    storedBase: p.basePoints,
    // Igual ao servidor: o campo, que a regra obriga a dizer a verdade.
    semFoto: p.semFoto === true,
    kcal: p.kcal ?? null,
    marcas: marcasValidas(p.kcalMarcas),
    // Para as condições de missão (o chip "🎯" do prato).
    price: p.price ?? null,
    placeKey: p.placeKey ?? "",
    canal: p.canal ?? null,
    ratingAvg: p.ratingAvg ?? null,
    ratingCount: p.ratingCount ?? 0,
  };
}

/** O prato tem foto? Pela capa ou pelo carrossel. É para DESENHAR: quem
    decide o custo no placar é o campo `semFoto`. */
export function temFoto(p: Pick<Plate, "photoPath" | "photos">): boolean {
  return !!p.photoPath || !!p.photos?.some((f) => !!f.photo);
}
